/**
 * Category Cockpit — the one way a write leaves this page (band B).
 *
 * Every action in the cockpit goes through `<Act>`, and `<Act>` is built around three rules that
 * the read-only version of this page could afford to leave implicit:
 *
 * 1. ⛔ NOTHING IS WRITTEN WITHOUT A CONFIRMATION THAT NAMES WHAT IS WRITTEN. Not "are you sure" —
 *    the table that changes, the reach (how many sites, products and pages the change touches), the
 *    spend when the endpoint calls a model, and the destruction when the handler destroys something.
 *    Those are `Confirm.writes` / `reach` / `cost` / `warn`, and a definition that carries none of
 *    them is a definition that has not been thought about.
 * 2. ⛔ THE OUTCOME IS READ FROM THE SERVER'S REPLY AND IT STAYS ON SCREEN. No toast: several of
 *    these endpoints report a partial success, a truncation or a spend that was not recorded, and
 *    that is precisely the text an operator needs to still be able to read a minute later. A failure
 *    shows the server's own message and status, never a rewritten one.
 *    ⛔⛔ AND IT IS HELD BY THE PAGE, NOT BY THE BUTTON. It lived on the button first, and the
 *    refresh in rule 3 unmounted the chapter the button was in — so the result of a write was
 *    destroyed by the re-read that same write triggered, which is the one moment it must survive.
 *    Measured in the browser against the extraction-hints action: the request went out, the reply
 *    said `truncated` with two attributes never researched, and the screen showed nothing at all.
 *    ⇒ `<Act>` REPORTS into the provider's log and `<ActLog>` renders it somewhere stable.
 * 3. ⛔ AFTER A WRITE THE PAGE RE-READS ITSELF. The cockpit's reads are cached in-process for 2–10
 *    minutes, so a screen that refetched without dropping that cache would show the pre-write
 *    numbers and look as if nothing had happened. `afterWrite` drops the sector's cache first, then
 *    refetches — in that order, and awaited, so what appears is what the database now holds.
 */
import React from 'react';
import { ActionError, type ActionOutcome, type ActionResult } from '@/services/categoryCockpitActions';

/** what the operator reads before the write happens */
export interface Confirm {
  /** the action, in the words of the person deciding: "Research all 10 aspects" */
  title: string;
  /** what is written, and where: name the table or the queue */
  writes: React.ReactNode;
  /** how far the change reaches — the same sentence the category header shows */
  reach?: React.ReactNode;
  /** what it costs when the endpoint calls a model; omit only when it calls none */
  cost?: React.ReactNode;
  /** what this destroys or arms, when it destroys or arms something */
  warn?: React.ReactNode;
  /**
   * a value the endpoint requires from the operator (a dismissal reason, a note). `initial` pre-fills
   * it with a suggestion the operator edits — the judge's finding turned into a sentence (band D).
   */
  ask?: { label: string; placeholder?: string; min?: number; initial?: string };
  /** the word on the button that commits: "Research", "Apply", "Dismiss" */
  verb: string;
  danger?: boolean;
}

/**
 * What a write is ABOUT: the sector, category and site it acts on, and the one row when it acts on
 * a row. It travels with the result into the log, because the log outlives the screen: a result
 * read on another category's page — or after leaving the cockpit — that does not say which
 * category it was about is a result that gets attributed to the wrong one.
 */
export interface Target {
  sector?: string;
  path?: string;
  site?: string | null;
  /** the row acted on (a gap, a note, a queued action), when the action is per row */
  item?: string;
}

export interface ActionDef {
  /** the button on the page */
  label: string;
  /** what the write is about — see Target */
  target?: Target;
  /**
   * The write's identity when two DIFFERENT buttons perform it — the same endpoint on the same
   * target from two screens (the category page's "Research all 10 aspects" and the queue's
   * "Research it again"). Their titles differ, so without it the in-flight guard held each apart
   * and a second click from the other screen paid for the same run again (codex dual-review, #6531).
   */
  op?: string;
  confirm: Confirm;
  run: (input: string) => Promise<ActionResult>;
  /** how to read this endpoint's reply; the raw body is always available under "what came back" */
  render?: (r: ActionResult) => React.ReactNode;
  /** true while the action cannot be offered, with the reason on the button */
  disabled?: string;
}

export interface ActProps {
  /** drop the sector's cached reads, then refetch the screen. Awaited by `<Act>`. */
  afterWrite: () => Promise<void>;
  /** the reach sentence for this category, so every confirmation says the same thing */
  reach?: React.ReactNode;
}

/** one finished write, kept by the PAGE so the refresh it triggers cannot erase it */
export interface Entry {
  key: number;
  kind: 'ok' | 'partial' | 'bad';
  title: string;
  target?: Target;
  body: React.ReactNode;
  raw: Record<string, unknown> | null;
}

interface Ctx extends ActProps {
  record: (e: Omit<Entry, 'key'>) => void;
  entries: Entry[];
  forget: (key: number) => void;
  /** how a screen inside the provider hands it the refresh to run after a write */
  register: (v: ActProps) => void;
}

const ActCtx = React.createContext<Ctx | null>(null);

/**
 * ⛔⛔ THE RESULT OF A WRITE BELONGS TO SOMETHING THAT DOES NOT UNMOUNT, AND "THE PAGE" WAS NOT IT.
 *
 * This provider has now been hoisted three times, each time above the branch that had just been
 * found to destroy it: the category's loading branch (round 3), the sector's (round 4), and now
 * the SITE's — `Shell` renders `{… && !sectorLoading && sectorId != null && children}`
 * (components/Shell.tsx:179), so switching site in the global selector removes the whole body
 * while the new site's sectors resolve. A paid action still in flight then records its outcome
 * into a provider nobody is rendering, and it is simply gone.
 *
 * 🔑 THE SHAPE, since hoisting one more level is what failed twice: the thing that outlives the
 * screen cannot be MOUNTED BY the screen. So the provider now owns the log and the screen
 * REGISTERS its refresh with it (`useProvideAct`) — the log sits outside `<Shell>` entirely and
 * there is no branch left above it to hoist past.
 */
/**
 * ⛔⛔⛔ THE LOG IS NOT REACT STATE ANY MORE, AND THAT IS THE POINT.
 *
 * This store has now been moved four times, each time above the boundary that had just been found
 * to destroy it: the category branch (round 3), the sector branch (round 4), the shell's own
 * children (round 8, the site switch) — and now the ROUTE, because `App.tsx:568-571` mounts the
 * four cockpit screens as separate route elements, so clicking "To decide" unmounts the whole book
 * page and everything it holds. Each hoist was correct and each one left a taller boundary above
 * it.
 *
 * 🔑 SO THE ANSWER IS NOT A TALLER HOIST. A store that lives in the tree can always be unmounted by
 * something further up; this one lives in the MODULE, and `record` is a plain function that works
 * whether or not anything is currently rendering. A write that finishes while the operator is on
 * another screen — or has left the cockpit altogether — still lands, and is there when they come
 * back. That is a deliberate change of behaviour: results now outlive the screen for the whole
 * session, until dismissed or until the page is reloaded. They are the evidence of a paid or
 * destructive write, and losing them is what sends an operator to retry it.
 */
const store = {
  entries: [] as Entry[],
  seq: 0,
  listeners: new Set<() => void>(),
  emit() { this.listeners.forEach((l) => l()); },
  subscribe(l: () => void) { store.listeners.add(l); return () => { store.listeners.delete(l); }; },
  snapshot() { return store.entries; },
  record(e: Omit<Entry, 'key'>) {
    store.seq += 1;
    // newest first, and every one kept: two writes in a row are two things that happened
    store.entries = [{ ...e, key: store.seq }, ...store.entries];
    store.emit();
  },
  forget(key: number) { store.entries = store.entries.filter((x) => x.key !== key); store.emit(); },
  /**
   * ⛔⛔ WHAT IS IN FLIGHT LIVES HERE TOO, FOR THE SAME REASON THE LOG DOES.
   * "Running" was the button's own state, so it died with the button: start a paid research,
   * open another chapter or the To-decide queue, come back, and the button was enabled again over
   * a request still being paid for — one more click and the provider was paid twice (codex review
   * minor on PR #6479). Keyed by `actKey`, so it is the SAME write on the same target that is
   * held, whichever component instance offers it.
   */
  running: new Set<string>(),
  begin(k: string) { store.running = new Set(store.running).add(k); store.emit(); },
  end(k: string) { const n = new Set(store.running); n.delete(k); store.running = n; store.emit(); },
};

/**
 * The identity of a write: its `op` when it declares one (the same write offered by two
 * buttons), otherwise its title (the action) and its target. Not the label — a label can carry a
 * count that a refresh changes mid-flight, and the guard must not let go when it does.
 */
export const actKey = (def: ActionDef): string => (def.op
  ? JSON.stringify(['op', def.op])
  : JSON.stringify([
    def.confirm.title, def.target?.sector ?? null, def.target?.path ?? null, def.target?.site ?? null, def.target?.item ?? null,
  ]));

/** test seam: the store outlives components by design, so a test that mounts twice must reset it */
export function clearActLog() { store.entries = []; store.seq = 0; store.running = new Set(); store.emit(); }

/** one line naming what a result was about — the category's leaf first, then where */
export function targetLine(t?: Target): string {
  if (!t) return '';
  return [t.path, t.sector, t.site].filter(Boolean).join(' · ');
}

export function ActProvider({ value, children }: { value?: ActProps; children: React.ReactNode }) {
  const entries = React.useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);
  // the screen's own refresh, registered from inside and replaced when the screen goes away
  const [registered, setRegistered] = React.useState<ActProps>({ afterWrite: async () => {} });
  const record = React.useCallback((e: Omit<Entry, 'key'>) => store.record(e), []);
  const forget = React.useCallback((key: number) => store.forget(key), []);
  // `value` is still honoured so a screen that genuinely owns its provider — and every test that
  // mounts one directly — keeps working unchanged; `registered` is what a hoisted provider uses.
  const live = value || registered;
  const ctx = React.useMemo(
    () => ({ ...live, record, entries, forget, register: setRegistered }),
    [live.afterWrite, live.reach, record, entries, forget],
  );
  return <ActCtx.Provider value={ctx}>{children}</ActCtx.Provider>;
}

/**
 * Register this screen's refresh with the provider that OUTLIVES it.
 *
 * ⛔ AND DEREGISTER ON THE WAY OUT, because the danger does not end when the screen does: the
 * old `afterWrite` closes over the old screen's state, so leaving it in place would let a write
 * that lands after a site switch report "done" and claim a refresh that reached nothing. On
 * unmount it is replaced by one that REFUSES, in the same words the in-place navigation guard
 * uses — the outcome is still recorded, and it says the screen was not refreshed.
 */
export function useProvideAct(value: ActProps, away: string) {
  const { register } = useAct();
  const { afterWrite, reach } = value;
  React.useEffect(() => { register({ afterWrite, reach }); }, [register, afterWrite, reach]);
  // mount-only, so the refusal is installed when the screen really goes — not on every re-render
  const awayRef = React.useRef(away);
  awayRef.current = away;
  React.useEffect(() => () => {
    register({ afterWrite: async () => { throw new Error(awayRef.current); } });
  }, [register]);
}

export function useAct(): Ctx {
  const c = React.useContext(ActCtx);
  // A write that cannot refresh the screen afterwards would leave the operator reading stale
  // numbers as if they were the result. That is a programming error, and it says so.
  if (!c) throw new Error('<Act> used outside <ActProvider>: an action needs the refresh that follows it');
  return c;
}

/**
 * Where the results of the writes appear. Put it somewhere the page does not re-mount — above the
 * chapter rail, above the question list — because the whole point is that it outlives the refresh.
 */
export function ActLog() {
  const { entries, forget } = useAct();
  if (!entries.length) return null;
  return (
    <div className="pmk-actlog">
      {entries.map((e) => (
        <div key={e.key} className={`pmk-note ${e.kind === 'ok' ? 'ok' : 'warn'} pmk-outcome`}>
          <div className="pmk-outcome-hd">
            <b>{e.title}</b>
            <button type="button" className="pmk-tool" onClick={() => forget(e.key)} aria-label="Close this result">×</button>
          </div>
          {e.target && targetLine(e.target) ? <div className="pmc-small pmc-mute pmk-outcome-target">on {targetLine(e.target)}</div> : null}
          {e.body}
          {e.raw ? <Raw body={e.raw} /> : null}
        </div>
      ))}
    </div>
  );
}

type Phase = 'idle' | 'asking' | 'running';

export function Act({ def, small }: { def: ActionDef; small?: boolean }) {
  const { afterWrite, reach, record } = useAct();
  const [phase, setPhase] = React.useState<Phase>('idle');
  const [input, setInput] = React.useState('');

  const min = def.confirm.ask?.min ?? 0;
  const inputOk = !def.confirm.ask || input.trim().length >= min;
  const key = actKey(def);
  // true while THIS write on THIS target is in flight anywhere — including from an instance of
  // this button that has since unmounted
  const busy = React.useSyncExternalStore(store.subscribe, () => store.running.has(key), () => store.running.has(key));

  async function go() {
    // a second instance, or a second click that raced the first: the write is already paid for
    if (store.running.has(key)) { setPhase('idle'); return; }
    store.begin(key);
    try {
      await run();
    } finally {
      store.end(key);
    }
  }

  async function run() {
    setPhase('running');
    let result;
    try {
      result = await def.run(input.trim());
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      const ae = err instanceof ActionError ? err : null;
      /**
       * ⛔⛔ "REFUSED" IS A CLAIM THAT NOTHING WAS WRITTEN, AND THIS BRANCH COULD NOT MAKE IT.
       * Three different outcomes arrived here and were all labelled the same way:
       *   - a refusal the client made before calling anything (`sent: false`) — a real no-op;
       *   - a server refusal — nothing written, by that route's own contract;
       *   - a PARTIAL application or a LOST REPLY — work done, money spent, screen stale.
       * The third is the one that mattered: `fill-gaps-with-data` commits each aspect in its own
       * transaction and then answers 200 `success: false` with the per-gap errors, so the operator
       * was told "refused", shown the string "HTTP 200", and left looking at pre-write numbers.
       * ⇒ The outcome is named for what it is, and ANYTHING THAT REACHED THE SERVER TRIGGERS THE
       *   RE-READ — a wasted refetch after a clean refusal costs one query; a skipped one after a
       *   partial write costs the operator their picture of the database.
       *
       * ⛔⛔ AND IT IS READ FROM ONE TOTAL FIELD, NOT RECOGNISED CODE BY CODE. The first version
       * of this branch reached "outcome unknown" only via `code === 'no_reply'`, so a reply that
       * stopped mid-body, an empty reply and a non-JSON reply — all of them writes that may have
       * completed — fell past it to "refused by the server". A default that means "refused" is
       * only safe if every other outcome is enumerated above it, and enumerating by code is how
       * that stops being true the next time an error is added. `outcome` has no default.
       */
      // An error that is not an ActionError was not classified by anyone, so it establishes
      // nothing — least of all that the write did not happen. `unknown` is the honest reading and
      // it costs one refetch; `refused` here would be the same unearned claim, made by the default
      // instead of by a branch.
      const outcome: ActionOutcome = ae ? ae.outcome : 'unknown';
      const noop = outcome === 'noop';
      let refreshErr: string | null = null;
      if (!noop) {
        try {
          await afterWrite();
        } catch (re) {
          refreshErr = re instanceof Error ? re.message : String(re);
        }
      }
      record({
        kind: 'bad',
        target: def.target,
        title: outcome === 'partial' ? `${def.confirm.title}: PARTLY APPLIED`
          : outcome === 'unknown' ? `${def.confirm.title}: outcome unknown`
            : outcome === 'noop' ? `${def.confirm.title}: refused, nothing was sent`
              : `${def.confirm.title}: refused by the server`,
        body: (
          <>
            <div>{err.message}</div>
            {outcome === 'partial' && (
              <div className="pmc-small pmk-warn" style={{ marginTop: 4 }}>
                Everything that did not fail was written and paid for — this is not a run you can simply repeat. The numbers below were re-read.
              </div>
            )}
            {outcome === 'unknown' && (
              <div className="pmc-small pmk-warn" style={{ marginTop: 4 }}>
                Do not retry before reading the screen: the request reached the network, so the write may have happened.
              </div>
            )}
            {ae && <div className="pmc-small pmc-mute" style={{ marginTop: 4 }}>HTTP {ae.status} · {ae.code}{noop ? ' · nothing left the browser' : ''}</div>}
            {refreshErr && (
              <div className="pmc-small pmk-warn" style={{ marginTop: 6 }}>
                And re-reading the page did not work either: {refreshErr}. Reload before acting on anything below.
              </div>
            )}
          </>
        ),
        raw: ae ? ae.body : null,
      });
      setPhase('idle');
      return;
    }
    // The write happened. It is recorded BEFORE the refresh, because the refresh can unmount this
    // component — and a result destroyed by its own re-read is the one loss this log exists for.
    let refreshErr: string | null = null;
    try {
      await afterWrite();
    } catch (e) {
      // A refresh that fails is NOT a write that failed. Conflating them would have an operator
      // run a paid research a second time.
      refreshErr = e instanceof Error ? e.message : String(e);
    }
    record({
      kind: result.partial ? 'partial' : 'ok',
      target: def.target,
      title: result.partial ? `${def.confirm.title}: accepted, not finished` : `${def.confirm.title}: done`,
      body: (
        <>
          {def.render ? def.render(result) : <ServerSaid body={result.body} />}
          {refreshErr && (
            <div className="pmc-small pmk-warn" style={{ marginTop: 6 }}>
              The write went through; re-reading the page did not: {refreshErr}. Reload to see the new numbers.
            </div>
          )}
        </>
      ),
      raw: result.body,
    });
    setPhase('idle');
  }

  return (
    <span className="pmk-act">
      <button
        type="button"
        className={`pmc-btn${def.confirm.danger ? ' pmk-danger' : ''}${small ? ' pmk-sm' : ''}`}
        disabled={!!def.disabled || phase !== 'idle' || busy}
        title={def.disabled || (busy && phase === 'idle' ? 'Already running — started earlier; this re-enables when it finishes, and its result appears in the log' : undefined)}
        onClick={() => { setPhase('asking'); setInput(def.confirm.ask?.initial || ''); }}
      >
        {phase === 'running' || busy ? `${def.confirm.verb}…` : def.label}
      </button>

      {phase === 'asking' && (
        <ConfirmDialog
          c={def.confirm}
          reach={reach}
          input={input}
          setInput={setInput}
          inputOk={inputOk}
          onCancel={() => setPhase('idle')}
          onGo={go}
        />
      )}
    </span>
  );
}

function ConfirmDialog({ c, reach, input, setInput, inputOk, onCancel, onGo }: {
  c: Confirm; reach?: React.ReactNode; input: string; setInput: (s: string) => void;
  inputOk: boolean; onCancel: () => void; onGo: () => void;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    // The commit button is NOT auto-focused: a return key still in flight from the page behind
    // would commit a write nobody read. The cancel is the safe default focus.
    const el = ref.current?.querySelector<HTMLElement>('[data-autofocus]');
    el?.focus();
  }, []);

  return (
    <div className="pmk-modal" role="dialog" aria-modal="true" aria-label={c.title} onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}>
      <div className="pmk-modal-box" ref={ref}>
        <h3>{c.title}</h3>
        <dl className="pmk-kv">
          <dt>What is written</dt><dd>{c.writes}</dd>
          {(c.reach || reach) && <><dt>Reach</dt><dd>{c.reach || reach}</dd></>}
          {c.cost && <><dt>Cost</dt><dd>{c.cost}</dd></>}
        </dl>
        {c.warn && <div className="pmk-note warn">{c.warn}</div>}
        {c.ask && (
          <label className="pmk-ask">
            <span>{c.ask.label}</span>
            <input
              className="pmc-input"
              value={input}
              placeholder={c.ask.placeholder}
              onChange={(e) => setInput(e.target.value)}
            />
            {!inputOk && <span className="pmc-small pmc-mute">at least {c.ask.min} characters — it is stored as the audit of this decision</span>}
          </label>
        )}
        <div className="pmk-modal-foot">
          <button type="button" className="pmc-btn" data-autofocus onClick={onCancel}>Cancel</button>
          <button type="button" className={`pmc-btn ${c.danger ? 'pmk-danger' : 'primary'}`} disabled={!inputOk} onClick={onGo}>{c.verb}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * The default reading of a reply: the server's own sentence when it wrote one, and the server's own
 * numbers when it did not.
 *
 * Three of these routes — discover-subcategories, fill-gaps-with-data, domain-knowledge — forward
 * their service's result with no envelope and no `message`, so "the endpoint answered without a
 * message" was true and useless: the counts an operator needs were one collapsed `<details>` away.
 * The scalars are shown as they came, unrenamed and uninterpreted, and anything structured stays in
 * "what came back" — this summarises the reply, it does not translate it.
 */
function ServerSaid({ body }: { body: Record<string, unknown> }) {
  const msg = body.message || body.error;
  if (typeof msg === 'string' && msg) return <div>{msg}</div>;
  const scalars = Object.entries(body).filter(([k, v]) => k !== 'success' && k !== 'ok' && (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean'));
  if (!scalars.length) return <div className="pmc-mute">The endpoint answered with nothing but an acknowledgement — what it returned is below.</div>;
  return <div>{scalars.map(([k, v]) => <span key={k} style={{ marginRight: 12 }}><span className="pmc-mute">{k}</span> <b>{String(v)}</b></span>)}</div>;
}

/** what came back, whole. Collapsed, because it is evidence rather than reading matter. */
function Raw({ body }: { body: Record<string, unknown> }) {
  return (
    <details className="pmk-raw">
      <summary className="pmc-small pmc-mute">what came back</summary>
      <pre className="pmk-json">{JSON.stringify(body, null, 1).slice(0, 4000)}</pre>
    </details>
  );
}

/** a row of actions under a chapter body */
export function Acts({ children }: { children: React.ReactNode }) {
  return <div className="pmk-actrow">{children}</div>;
}
